import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// CONEK-9198 — "scan the leg before inspecting it": the sheet that asks for
// the scan.
//
// Connects to data-controller="inspections--scan-gate" on the wrapper that
// `inspections/shared/_scan_gate_sheet` renders once per page around its
// Ui::ModalComponent (a `ui--modal` <dialog>), next to the disclaimer.
//
// Two places ask, both inside `[data-inspection-scan-gate]` (the marker's value
// is the scan-check URL):
//
//   * On the leg (the leg page's "No damage" and "Inspection", an alert's
//     "Start inspection", the inspections list's "Start inspection", a
//     follow-up's "Load previous data" / "Create blank"): a capture-phase
//     `click` listener on `window` holds back the press on a control that
//     starts an inspection — the controls the disclaimer holds (CONEK-8782),
//     or one marked `data-inspection-scan-gate-control` where there is no
//     disclaimer. `window` capture runs before the disclaimer's `document`
//     capture, so the scan comes FIRST. After the scan the click is replayed
//     and the disclaimer asks next. The answer travels with it: every form
//     inside the marker gets `scan_proof` (or `skip`), a start link gets it in
//     its query (the start screen carries it on to Start), and the marker is
//     marked passed so the replay, and the disclaimer's own replay, go
//     through. The code never travels: a matching check answers with a signed
//     proof (Inspections::ScanProof), and that is what the start carries.
//   * On the start screen: a capture-phase `submit` listener holds back Start
//     and No damage, unless the form already carries the answer from the leg.
//
// A page rendered inside a valid location context (a scan in the last ten
// minutes) carries `data-inspection-scan-gate-valid-until` (epoch ms): until
// then nothing asks, after it the sheet asks again. A submitter marked
// `data-inspection-scan-gate-exempt` starts nothing (a verdict on the viewer's
// open aisle walk) and always goes through.
//
// The sheet opens on the camera: in a browser html5-qrcode's camera runs
// inside the dialog, with "Continue without scanning" (rendered only for
// inspection.skip_scan) under it; in the app the native `bridge--barcode`
// scanner opens over the page (through `iot--qr-scan`), and the sheet keeps a
// Scan button to open it again. Camera only. The X and Esc cancel. A camera
// keeps decoding while the leg's QR is in view, so a code is checked once at
// a time. A decoded code is checked against the leg first (the scan-check
// endpoint, the same decision the start action takes). A wrong code closes
// the sheet and the server's toast says whose QR it was; a check that could
// not be made is said INLINE (the dialog is in the top layer, where no toast
// can reach), the sheet stays open, and the same code is not sent again for a
// moment. A match,
// or a skip, goes on with the press that asked — a held submit is re-submitted
// with the button that was pressed, so the start screen's "No damage" keeps
// its `formaction`. The start action decides again; this is convenience only.
//
// CONEK-9199 — the aisle walk's start and finish: a marker carrying
// `data-inspection-scan-gate-group` (the aisle; `-inspection`, the walk, at
// Finish) instead of a leg. The check is then asked which END of the aisle the
// code is, and the answer's proof goes on with the press — never a leg id, so
// the start stays an aisle start. `data-inspection-scan-gate-hint` says which
// leg to scan.
//
// CONEK-9309 — at that start or finish there is no "this leg" yet: the sheet's
// description and camera instruction say the aisle's end (the aisle values),
// and the next leg's sheet on the same page puts the default text back.
//
// CONEK-9313 — for a holder of inspection.manual_code the sheet also has a
// field for the leg's code, in its own small form (`manualForm`). A typed code
// goes the way a decode goes — the same `barcode:scanned` hand-off, the same
// check and the same answer — with `source: "manual"`, so the proof the check
// hands back records the start as `manual`. A refusal of a typed code is said
// under the field instead of closing the sheet, and a corrected code is checked
// at once (no quiet window: nobody types the same wrong code ten times a
// second).
const MARKER_SELECTOR = "[data-inspection-scan-gate]"
const PASSED_ATTRIBUTE = "data-inspection-scan-gate-passed"
const VALID_UNTIL_ATTRIBUTE = "data-inspection-scan-gate-valid-until"
// A control that asks for the scan as soon as the page shows it: the start
// screen opened without a scan, whose form the server holds back.
const AUTOSTART_SELECTOR = "[data-inspection-scan-gate-autostart]"
const AUTOSTARTED_ATTRIBUTE = "data-inspection-scan-gate-autostarted"
// A start link's href before the sheet added its answer to it.
const ORIGINAL_HREF_ATTRIBUTE = "data-inspection-scan-gate-href"
// What a start carries after the sheet: `scan_proof` (the signed proof the
// check answered with; the code itself never leaves the sheet) or `skip`.
// `scan_code` is still cleared from a form that has one.
const GATE_FIELDS = ["scan_proof", "skip", "scan_code"]
// The controls that start an inspection: the disclaimer's markers.
const DISCLAIMER_SELECTOR =
  "[data-inspection-disclaimer], [data-inspection-scan-gate-control]"
const DISCLAIMER_SCOPE_SELECTOR = "[data-inspection-disclaimer-scope]"
const SCOPED_CONTROL_SELECTOR =
  'a[href], button, input[type="submit"], input[type="image"]'

// How long a refused code is not checked again: the camera decodes it ten
// times a second for as long as it is in view.
const REFUSED_CODE_QUIET_MS = 2000

// Forms and controls let through for one replay. Module-level, so a second
// controller instance (two layouts mid-swap) lets the replay through too;
// cleared as soon as the replay returns, so it never lets a later, real press
// through.
const approved = new WeakSet()

export default class extends Controller {
  static targets = [
    "error",
    "hint",
    "scanner",
    "camera",
    "instruction",
    "cameraNote",
    "scanButton",
    "manualForm",
    "manualInput",
    "manualError",
  ]
  static values = {
    error: String,
    cameraError: String,
    legDescription: String,
    legInstruction: String,
    aisleDescription: String,
    aisleInstruction: String,
  }

  connect() {
    this.pending = null
    this.stopCamera = null
    this.checking = false
    this.refused = null
    this.dialog = this.element.querySelector("dialog")
    // The modal's description line: the <p> its aria-describedby names.
    const describedBy = this.dialog?.getAttribute("aria-describedby")
    this.description = describedBy ? document.getElementById(describedBy) : null
    // Said, not swallowed: without that line the aisle's start and finish
    // would quietly show the leg's text (the markup is conek_ui's).
    if (describedBy && !this.description) {
      console.warn(
        `inspections--scan-gate: no description #${describedBy}; the aisle text cannot be shown`,
      )
    }

    this.handleClick = this.handleClick.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleDialogClose = this.handleDialogClose.bind(this)
    this.handleBeforeCache = this.handleBeforeCache.bind(this)
    this.handlePageShow = this.handlePageShow.bind(this)

    window.addEventListener("click", this.handleClick, true)
    document.addEventListener("submit", this.handleSubmit, true)
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)
    window.addEventListener("pageshow", this.handlePageShow)
    this.dialog?.addEventListener("close", this.handleDialogClose)

    // From here on presses come to this controller, not to the loading
    // guard in the layout's <head> (`inspections/shared/_scan_gate_guard`).
    window.__inspectionScanGateReady = true

    // After every other controller on the page has connected (the dialog's
    // ui--modal among them).
    setTimeout(() => {
      this.answerHeldPress()
      this.autostart()
    }, 0)
  }

  // A start pressed while the page was still loading, held by the guard in
  // `_scan_gate_guard`: press it again now that the sheet can answer.
  answerHeldPress() {
    const held = window.__inspectionScanGatePending
    window.__inspectionScanGatePending = null
    if (held instanceof Element && held.isConnected) held.click()
  }

  // The start screen opened without a scan asks for it straight away, once.
  // Not on Turbo's preview of a cached page: the real page follows at once.
  autostart() {
    if (!this.element.isConnected || this.pending || this.dialog?.open) return
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    const control = document.querySelector(AUTOSTART_SELECTOR)
    if (!control || control.hasAttribute(AUTOSTARTED_ATTRIBUTE)) return

    control.setAttribute(AUTOSTARTED_ATTRIBUTE, "")
    control.click()
  }

  disconnect() {
    window.removeEventListener("click", this.handleClick, true)
    document.removeEventListener("submit", this.handleSubmit, true)
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
    window.removeEventListener("pageshow", this.handlePageShow)
    this.dialog?.removeEventListener("close", this.handleDialogClose)
    this.pending = null
    this.stopScanning()
  }

  // The press on the leg: before the disclaimer, which holds the same click.
  handleClick(event) {
    // A wrapper removed without Stimulus noticing must not keep intercepting:
    // the sheet of the CURRENT page is the one that answers.
    if (!this.element.isConnected) {
      this.disconnect()
      return
    }
    if (event.button !== 0) return

    const control = startControlOf(event.target)
    if (!control || approved.has(control)) return

    const marker = control.closest(MARKER_SELECTOR)
    const checkUrl = marker?.getAttribute("data-inspection-scan-gate")
    if (!checkUrl || passed(marker)) return

    // No leg yet (the inspections list's "Start inspection"): a start LINK
    // lets the scan choose it. An aisle marker asks for an end of the aisle.
    // Anything else has nothing to scan for.
    const aisle = aisleOf(marker)
    const assetId = aisle ? null : assetIdOfControl(control, marker)
    if (!assetId && !aisle && !(control instanceof HTMLAnchorElement)) return

    event.preventDefault()
    event.stopImmediatePropagation()

    this.ask({
      control,
      marker,
      assetId: assetId ? String(assetId) : null,
      aisle,
      checkUrl,
    })
  }

  // Start and No damage on the start screen.
  handleSubmit(event) {
    if (!this.element.isConnected) {
      this.disconnect()
      return
    }

    const form = event.target
    if (!(form instanceof HTMLFormElement)) return

    const marker = form.closest(MARKER_SELECTOR)
    const checkUrl = marker?.getAttribute("data-inspection-scan-gate")
    if (!checkUrl) return
    if (approved.has(form) || passed(marker) || answered(form)) return

    const submitter = submitterOf(event, form)
    if (submitter?.hasAttribute("data-inspection-scan-gate-exempt")) return
    // No leg chosen yet (the desktop start screen's picker): there is nothing
    // to scan for, and the start action answers that on its own. An aisle
    // start asks for an end of the aisle.
    const aisle = aisleOf(marker)
    const assetId = aisle
      ? null
      : assetIdOf(form, submitter) ||
        marker.getAttribute("data-inspection-scan-gate-asset")
    if (!assetId && !aisle) return

    event.preventDefault()
    event.stopImmediatePropagation()

    this.ask({
      form,
      submitter,
      assetId: assetId ? String(assetId) : null,
      aisle,
      checkUrl,
    })
  }

  ask(request) {
    // Already asking: ignore the extra press.
    if (!this.dialog || this.dialog.open || this.pending) return

    this.pending = request
    this.checking = false
    this.refused = null
    this.showError(null)
    this.resetManual()
    this.showHint(request.aisle?.hint)
    this.showCopy(Boolean(request.aisle))
    this.openModal()
    this.startScanning()
  }

  // "Scan QR code": the app's native scanner again, or a browser camera that
  // could not open the first time.
  scan() {
    if (this.pending) this.startScanning()
  }

  async startScanning() {
    const request = this.pending
    const scanner = this.qrScanController()
    this.showCameraNote(null)

    // The app: the native scanner takes the screen; the sheet waits under it.
    if (scanner?.bridgeBarcodeOutlets?.[0]) {
      this.showCamera(false)
      this.showScanButton(true)
      scanner.open()
      return
    }

    if (!this.hasCameraTarget || this.stopCamera) return
    this.showScanButton(false)
    this.showCamera(true)
    try {
      const stop = await this.startCamera(this.cameraTarget, (text) =>
        this.decoded(text),
      )
      // Closed, or another press took over, while the camera was starting
      // (its permission prompt can wait as long as the reader does).
      if (this.pending !== request || !this.dialog?.open) {
        await stop()
        return
      }
      this.stopCamera = stop
    } catch (error) {
      console.warn("inspections--scan-gate camera failed:", error)
      if (this.pending !== request) return
      this.showCamera(false)
      this.showCameraNote(this.cameraErrorValue)
      this.showScanButton(true)
    }
  }

  // Starts html5-qrcode's CAMERA scanner in `region` (never its file
  // scanner) and resolves to the function that stops it.
  async startCamera(region, onDecode) {
    const { Html5Qrcode } = await import("html5-qrcode")
    const camera = new Html5Qrcode(region.id)
    const stop = async () => {
      try {
        await camera.stop()
      } catch {
        /* not running */
      }
      try {
        camera.clear()
      } catch {
        /* already cleared */
      }
    }
    try {
      await camera.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        onDecode,
        () => {
          /* per-frame misses */
        },
      )
    } catch (error) {
      await stop()
      throw error
    }
    return stop
  }

  stopScanning() {
    const stop = this.stopCamera
    this.stopCamera = null
    if (stop) stop()
    this.showCamera(false)
  }

  // A camera decode, handed on the way the app's scanner hands one: as
  // `iot--qr-scan`'s re-dispatch from the scanner's input.
  decoded(text, { manual = false } = {}) {
    const input = this.hasScannerTarget
      ? this.scannerTarget.querySelector("input[type=hidden]")
      : null
    if (!input) return

    input.value = text
    input.dispatchEvent(
      new CustomEvent("barcode:scanned", {
        bubbles: true,
        detail: { value: text, source: "iot--qr-scan", manual },
      }),
    )
  }

  // CONEK-9313 — the typed code's form. Its submit never leaves the sheet:
  // nothing navigates, and no start form sees it.
  typed(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.pending || !this.hasManualInputTarget) return

    const code = this.manualInputTarget.value.trim()
    if (code.length === 0) {
      this.manualInputTarget.focus()
      return
    }
    this.showManualError(null)
    this.decoded(code, { manual: true })
  }

  // The scanner's `barcode:scanned`. Only its own re-dispatch (`source:
  // "iot--qr-scan"`) is a decode: the native bridge's raw reply reaches this
  // element too, possibly as a cancellation.
  async scanned(event) {
    const detail = event?.detail || {}
    if (detail.source !== "iot--qr-scan") return

    const code = typeof detail.value === "string" ? detail.value.trim() : ""
    const manual = detail.manual === true
    const request = this.pending
    // The dialog's `close` event (which drops `pending`) is queued after
    // `open` turns false; a decode landing in between is still a cancel.
    if (!request || !this.dialog?.open || code.length === 0) return
    if (this.checking || (!manual && this.recentlyRefused(code))) return

    this.checking = true
    let answer
    try {
      answer = await this.check(request, code, { manual })
    } catch {
      answer = null
    } finally {
      this.checking = false
    }
    // Cancelled, or another press took over, while the check was in flight.
    if (this.pending !== request) return

    if (answer?.ok) {
      // A scan that chose the leg opens the start screen on it (never an
      // aisle's: its end leg is proven, not chosen).
      if (!request.assetId && !request.aisle && answer.asset_id) {
        request.chosenAssetId = String(answer.asset_id)
      }
      this.proceed({ scan_proof: String(answer.proof ?? "") })
    } else if (manual) {
      // A typed code stays in the field, to be corrected.
      this.showManualError(answer?.message || this.errorValue)
    } else if (answer?.toast) {
      // A wrong code: the sheet closes and the server's toast says why.
      this.refuse(answer.toast)
    } else {
      // The check itself failed (offline, rate-limited): say so in the sheet,
      // which stays open to try again.
      this.refused = { code, at: Date.now() }
      this.showError(answer?.message || this.errorValue)
    }
  }

  // Closes the sheet and shows the server-rendered toast (a Turbo Stream
  // appending Ui::ToastComponent, ToastHelper#toast_stream) once the dialog
  // has left the top layer: nothing renders above an open showModal() dialog.
  refuse(toastStream) {
    this.pending = null
    this.stopScanning()

    const show = () => window.Turbo?.renderStreamMessage(toastStream)
    if (this.dialog?.open) {
      this.dialog.addEventListener("close", show, { once: true })
      this.closeModal()
    } else {
      show()
    }
  }

  recentlyRefused(code) {
    return (
      this.refused?.code === code &&
      Date.now() - this.refused.at < REFUSED_CODE_QUIET_MS
    )
  }

  // "Continue without scanning" — rendered only for inspection.skip_scan; the
  // start action refuses a skip from anybody else whatever this sends.
  skip() {
    if (this.pending) this.proceed({ skip: "1" })
  }

  handleDialogClose() {
    // Every way out but a proceed (X, Esc) abandons the request. The camera
    // is never left running in a dialog nobody can see.
    this.pending = null
    this.stopScanning()
  }

  // The page is about to go into Turbo's cache, to come back on Back. What it
  // knows about the scan is only true NOW: the location context it was
  // rendered in may be replaced by a scan of another leg before the reader
  // comes back, and a proof is spent by one start. So the cached copy keeps
  // none of it and asks again (the server would refuse a stale answer anyway).
  handleBeforeCache() {
    this.pending = null
    this.stopScanning()
    if (this.dialog?.open) this.dialog.close()
    this.showHint(null)
    this.showCopy(false)
    this.resetManual()
    forgetAnswers(document)
  }

  // The same for the browser's own back-forward cache, which a full page load
  // (a `data-turbo="false"` start form) goes through instead of Turbo's.
  handlePageShow(event) {
    if (!event.persisted) return
    this.handleBeforeCache()
  }

  // --- helpers ---

  // A POST, so the code rides in the body and never in a URL (proxy and
  // access logs included).
  async check(request, code, { manual = false } = {}) {
    const url = new URL(request.checkUrl, window.location.origin)
    // Without a leg the server answers which leg the code is; for an aisle,
    // whether it is an end the start (or this walk's finish) accepts.
    const body = request.aisle
      ? {
          asset_group_id: request.aisle.groupId,
          ...(request.aisle.inspectionId
            ? { inspection_id: request.aisle.inspectionId }
            : {}),
          code,
        }
      : request.assetId
        ? { asset_id: request.assetId, code }
        : { code }
    // Only a holder of inspection.manual_code has the field; the server
    // refuses this from anybody else.
    if (manual) body.source = "manual"

    const response = await post(url.pathname, {
      body,
      responseKind: "json",
    })
    return response.ok ? await response.json : null
  }

  proceed(fields) {
    const request = this.pending
    this.pending = null
    this.stopScanning()
    this.closeModal()

    if (request.control) {
      // Next task, so the sheet's own `close` has run before the disclaimer
      // opens.
      setTimeout(() => this.replayClick(request, fields), 0)
    } else {
      this.resubmit(request, fields)
    }
  }

  replayClick({ control, marker, chosenAssetId }, fields) {
    // Replaced by a Turbo Stream meanwhile: the next press asks again.
    if (!control.isConnected) return

    marker.setAttribute(PASSED_ATTRIBUTE, "")
    for (const form of marker.querySelectorAll("form")) {
      setGateFields(form, fields)
    }
    if (control.form) setGateFields(control.form, fields)
    for (const link of [marker, ...marker.querySelectorAll("a[href]")]) {
      if (link instanceof HTMLAnchorElement && startControlOf(link) === link) {
        if (!link.hasAttribute(ORIGINAL_HREF_ATTRIBUTE)) {
          link.setAttribute(ORIGINAL_HREF_ATTRIBUTE, link.getAttribute("href"))
        }
        link.setAttribute(
          "href",
          withGateFields(
            link.getAttribute("href"),
            chosenAssetId ? { ...fields, asset_id: chosenAssetId } : fields,
          ),
        )
      }
    }

    approved.add(control)
    try {
      control.click()
    } finally {
      approved.delete(control)
    }
  }

  resubmit({ form, submitter }, fields) {
    if (!form.isConnected) return

    setGateFields(form, fields)
    approved.add(form)
    try {
      form.requestSubmit(submitter?.isConnected ? submitter : undefined)
    } finally {
      approved.delete(form)
    }
  }

  qrScanController() {
    if (!this.hasScannerTarget) return null
    return this.application.getControllerForElementAndIdentifier(
      this.scannerTarget,
      "iot--qr-scan",
    )
  }

  showCamera(visible) {
    if (this.hasCameraTarget) this.cameraTarget.hidden = !visible
    if (this.hasInstructionTarget) this.instructionTarget.hidden = !visible
  }

  showScanButton(visible) {
    if (this.hasScanButtonTarget) this.scanButtonTarget.hidden = !visible
  }

  showCameraNote(message) {
    if (!this.hasCameraNoteTarget) return
    this.cameraNoteTarget.textContent = message ?? ""
    this.cameraNoteTarget.hidden = !message
  }

  showHint(message) {
    if (!this.hasHintTarget) return
    this.hintTarget.textContent = message ?? ""
    this.hintTarget.hidden = !message
  }

  // The aisle's start or finish names the aisle end; anything else, the leg.
  // Both texts come from the server (the partial's values), never from the
  // page: a page Turbo restored from its cache, or a reconnect, may still
  // show the aisle's.
  showCopy(aisle) {
    this.setCopy(
      this.description,
      aisle ? this.aisleDescriptionValue : this.legDescriptionValue,
    )
    this.setCopy(
      this.hasInstructionTarget ? this.instructionTarget : null,
      aisle ? this.aisleInstructionValue : this.legInstructionValue,
    )
  }

  // A missing value is said, not papered over with the other text.
  setCopy(element, text) {
    if (!element) return
    if (!text) {
      console.warn("inspections--scan-gate: a sheet text value is empty")
      return
    }
    element.textContent = text
  }

  showManualError(message) {
    if (!this.hasManualErrorTarget) return

    this.manualErrorTarget.textContent = message ?? ""
    this.manualErrorTarget.hidden = !message
  }

  resetManual() {
    if (this.hasManualInputTarget) this.manualInputTarget.value = ""
    this.showManualError(null)
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message ?? ""
    this.errorTarget.hidden = !message
  }

  modalController() {
    if (!this.dialog) return null
    return this.application.getControllerForElementAndIdentifier(
      this.dialog,
      "ui--modal",
    )
  }

  openModal() {
    const controller = this.modalController()
    if (controller) {
      controller.open()
    } else if (this.dialog && !this.dialog.open) {
      this.dialog.showModal()
    }
  }

  closeModal() {
    const controller = this.modalController()
    if (controller) {
      controller.close()
    } else if (this.dialog?.open) {
      this.dialog.close()
    }
  }
}

// A control that starts an inspection: one the disclaimer holds — marked
// itself, or a link or submit button inside a disclaimer scope (components
// that take no `data:` on their own controls) — or one marked for the scan
// alone (`data-inspection-scan-gate-control`).
function startControlOf(target) {
  if (!(target instanceof Element)) return null

  const marked = target.closest(DISCLAIMER_SELECTOR)
  if (marked) return marked

  const control = target.closest(SCOPED_CONTROL_SELECTOR)
  if (!control?.closest(DISCLAIMER_SCOPE_SELECTOR)) return null
  if (control instanceof HTMLButtonElement && control.type !== "submit") {
    return null
  }
  return control
}

// Nothing to ask: the leg was scanned on this page, or the page was rendered
// inside a location context that has not run out yet.
function passed(marker) {
  if (marker.hasAttribute(PASSED_ATTRIBUTE)) return true

  const validUntil = Number(marker.getAttribute(VALID_UNTIL_ATTRIBUTE))
  return Boolean(validUntil) && Date.now() < validUntil
}

// The start screen carries on what the leg page's sheet answered.
function answered(form) {
  return GATE_FIELDS.some((name) => {
    const input = form.querySelector(`input[type=hidden][name="${name}"]`)
    return Boolean(input?.value)
  })
}

function setGateFields(form, fields) {
  for (const input of form.querySelectorAll("input[type=hidden]")) {
    if (GATE_FIELDS.includes(input.name)) input.remove()
  }
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    form.appendChild(input)
  }
}

// Drops every answer the page holds: the server's "valid until", a scan on
// this page, the answer added to a form or a link.
function forgetAnswers(root) {
  for (const marker of root.querySelectorAll(MARKER_SELECTOR)) {
    marker.removeAttribute(VALID_UNTIL_ATTRIBUTE)
    marker.removeAttribute(PASSED_ATTRIBUTE)
    for (const input of marker.querySelectorAll("input[type=hidden]")) {
      if (GATE_FIELDS.includes(input.name)) input.remove()
    }
  }
  for (const control of root.querySelectorAll(`[${AUTOSTARTED_ATTRIBUTE}]`)) {
    control.removeAttribute(AUTOSTARTED_ATTRIBUTE)
  }
  for (const link of root.querySelectorAll(`[${ORIGINAL_HREF_ATTRIBUTE}]`)) {
    link.setAttribute("href", link.getAttribute(ORIGINAL_HREF_ATTRIBUTE))
    link.removeAttribute(ORIGINAL_HREF_ATTRIBUTE)
  }
}

function withGateFields(href, fields) {
  const url = new URL(href, window.location.origin)
  for (const name of GATE_FIELDS) url.searchParams.delete(name)
  for (const [name, value] of Object.entries(fields)) {
    url.searchParams.set(name, value)
  }
  return url.origin === window.location.origin
    ? `${url.pathname}${url.search}${url.hash}`
    : url.toString()
}

// An aisle marker (CONEK-9199): the aisle, the walk at Finish, and the hint.
function aisleOf(marker) {
  const groupId = marker.getAttribute("data-inspection-scan-gate-group")
  if (!groupId) return null

  return {
    groupId,
    inspectionId: marker.getAttribute("data-inspection-scan-gate-inspection"),
    hint: marker.getAttribute("data-inspection-scan-gate-hint"),
  }
}

// The leg a press on the leg page is for: the marker names it, else the
// link's or the form's.
function assetIdOfControl(control, marker) {
  const named = marker.getAttribute("data-inspection-scan-gate-asset")
  if (named) return named

  if (control instanceof HTMLAnchorElement) {
    return new URL(control.href, window.location.origin).searchParams.get(
      "asset_id",
    )
  }
  return control.form ? assetIdOf(control.form, control) : null
}

// The leg the start is for: a field (the start screens, `button_to` params:)
// or the action's query string (the phone leg page's ActionSlot `button_to`,
// whose href names the leg).
function assetIdOf(form, submitter) {
  const field = new FormData(form, submitter).get("asset_id")
  if (field) return field

  const action = submitter?.getAttribute("formaction") || form.action
  return new URL(action, window.location.origin).searchParams.get("asset_id")
}

// The button that was pressed, when it belongs to this form: `FormData` and
// `requestSubmit` both throw on a submitter from another form.
function submitterOf(event, form) {
  const submitter = event.submitter
  return submitter && submitter.form === form ? submitter : undefined
}
